import { useCallback, useMemo, useState } from 'react';
import { staleEditOf, type StaleEdit } from '../../../utils/staleEdit';
import { mergeStaleEdit, type StaleFieldStatus } from './mergeStaleEdit';

export interface StaleConflict<T> {
  who: StaleEdit;
  /** The record as it is now, from the reader's own (masked) read. */
  theirs: T;
  status: Partial<Record<keyof T & string, StaleFieldStatus>>;
}

export interface StaleResolution<T> {
  /** What the form should hold now: their changes in, the reader's kept. */
  merged: T;
  /** The new base: dirty-tracking and the next save compare against this. */
  theirs: T;
}

/**
 * State for one form's refused save (option C, docs/architecture/concurrent-edits.md).
 *
 * On a save error, call {@link resolve} with what the form loaded (`base`),
 * what it holds (`mine`) and how to re-read the record. A stale refusal
 * re-reads, merges and returns the form's new values and base, and the
 * screen keeps everything typed; any other error returns null for the caller's
 * usual handling. The re-read is the screen's own read, so a masked field
 * never appears.
 */
export function useStaleEdit<T extends Record<string, unknown>>(
  fields: readonly (keyof T & string)[],
) {
  const [conflict, setConflict] = useState<StaleConflict<T> | null>(null);

  const resolve = useCallback(
    async (
      error: unknown,
      args: { base: T; mine: T; reload: () => Promise<T> },
    ): Promise<StaleResolution<T> | null> => {
      const who = staleEditOf(error);
      if (!who) return null;
      const theirs = await args.reload();
      const { merged, status } = mergeStaleEdit(args.base, args.mine, theirs, fields);
      setConflict({ who, theirs, status });
      return { merged, theirs };
    },
    [fields],
  );

  /** "Use theirs" on one field: the field now matches the record and is no longer a clash. */
  const settle = useCallback((field: keyof T & string) => {
    setConflict((current) =>
      current ? { ...current, status: { ...current.status, [field]: 'theirs' } } : current,
    );
  }, []);

  const clear = useCallback(() => setConflict(null), []);

  const clashes = useMemo(
    () =>
      conflict
        ? (Object.keys(conflict.status) as (keyof T & string)[]).filter(
            (field) => conflict.status[field] === 'clash',
          )
        : [],
    [conflict],
  );

  return { conflict, resolve, settle, clear, clashes };
}
